import { useEffect, useMemo, useState, useCallback } from "react";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import Loader from "../../Utility/Loader/Loader";
import GlobalFilter from "../../Utility/GlobalFilter/GlobalFilter";
import Pagination from "../../Utility/Pagination/Pagination";
import { SortableHeader, useSorting } from "../../Utility/Sorting";
import { RiLock2Fill } from "react-icons/ri";
import { formatDate } from "../../utils/dateUtils";
import {
  displayMonthKeyToIsoMonth,
  generateLockMonthWindowOptions,
} from "../../utils/monthLockApi";

const defaultDraftFilter = () => ({
  /** YYYY-MM or "" = all months */
  month: "",
  /** "" = Global (no client_id); otherwise client UUID */
  clientId: "",
});

/**
 * @param {Array<{ id?: string, clientId?: string, clientName?: string, name?: string }>} clients
 */
function buildClientDropdownOptions(clients = []) {
  const seen = new Set();
  const rest = [];
  for (const c of clients) {
    const id = c?.id ?? c?.clientId;
    const label = (c?.clientName ?? c?.name ?? "").trim();
    if (!id || !label) continue;
    const key = String(id);
    if (seen.has(key)) continue;
    seen.add(key);
    rest.push({ value: key, label });
  }
  rest.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  /** Global first, then A–Z clients */
  return [{ value: "", label: "Global" }, ...rest];
}

const MonthLockingSection = ({
  monthLocks,
  loading,
  clients = [],
  onOpenLockModal,
  onOpenUnlockModal,
  onRequestMonthLocks,
  /** When false, row “Lock” is not shown for unlocked months. */
  canLockMonth = false,
  /** When false, row “Unlock” is not shown for locked months. */
  canUnlockMonth = false,
}) => {
  const showActionsColumn = Boolean(canLockMonth || canUnlockMonth);
  const tableColSpan = showActionsColumn ? 6 : 5;
  const itemsPerPage = 5;
  const [draftFilter, setDraftFilter] = useState(defaultDraftFilter);
  const [currentPage, setCurrentPage] = useState(1);

  const monthOptions = useMemo(() => {
    const opts = [{ value: "", label: "All months" }];
    const windowOpts = generateLockMonthWindowOptions(24, 6);
    windowOpts.forEach((o) => {
      const iso = displayMonthKeyToIsoMonth(o.value);
      if (iso) opts.push({ value: iso, label: o.label });
    });
    return opts;
  }, []);

  const clientOptions = useMemo(() => buildClientDropdownOptions(clients), [clients]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setDraftFilter((prev) => ({ ...prev, [name]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    onRequestMonthLocks?.({
      month: draftFilter.month || undefined,
      client_id: draftFilter.clientId || undefined,
    });
    setCurrentPage(1);
  }, [draftFilter, onRequestMonthLocks]);

  const handleFilterReset = useCallback(() => {
    setDraftFilter(defaultDraftFilter());
    onRequestMonthLocks?.({});
    setCurrentPage(1);
  }, [onRequestMonthLocks]);

  const listingFilter = useMemo(
    () => ({
      month: draftFilter.month,
      clientId: draftFilter.clientId,
    }),
    [draftFilter]
  );

  const fields = useMemo(
    () => [
      {
        type: "dropdown",
        name: "month",
        label: "Month",
        options: monthOptions,
        placeholder: "All months",
        searchable: true,
        zIndex: true,
        clearable: false,
        preserveOptionOrder: true,
      },
      {
        type: "dropdown",
        name: "clientId",
        label: "Client",
        options: clientOptions,
        placeholder: "Global",
        searchable: true,
        zIndex: true,
        clearable: false,
        preserveOptionOrder: true,
      },
    ],
    [monthOptions, clientOptions]
  );

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(monthLocks, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  useEffect(() => {
    if (sortKey) {
      setCurrentPage(1);
    }
  }, [sortKey, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMonths = sortedData.slice(startIndex, endIndex);

  const getStatusStyles = (status) => {
    const baseClass = "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium";
    const normalized = (status || "").toLowerCase();

    if (normalized === "locked") {
      return {
        container: `${baseClass} bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]`,
        icon: "text-[#B91C1C]",
      };
    }

    if (normalized === "unlocked") {
      return {
        container: `${baseClass} bg-[#ECFDF5] text-[#166534] border border-[#A7F3D0]`,
        icon: "text-[#16A34A]",
      };
    }

    return {
      container: `${baseClass} bg-[var(--bg-secondary)] text-[var(--text-secondary)]`,
      icon: "text-[var(--text-secondary)]",
    };
  };

  return (
    <div className="bg-surface-primary rounded-lg border border-border-primary flex flex-col overflow-hidden">
      <div className="flex flex-col gap-2 p-4 border-b border-border-primary">
        <GlobalFilter
          listingFilter={listingFilter}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          fields={fields}
          columns={2}
          defaultExpanded={true}
          containerClassName="bg-transparent border-0 shadow-none mb-0 p-0"
          buttonWidth="w-full md:w-auto"
          flexClassName="flex flex-wrap gap-2 items-end"
        />
      </div>

      <div className="relative overflow-x-auto p-2">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-primary/40 z-10">
            <Loader />
          </div>
        )}
        <table className="min-w-full divide-y divide-border-primary clients-table text-sm">
          <thead className="bg-surface-secondary">
            <tr>
              <SortableHeader
                columnKey="month"
                label="Month"
                onSort={handleSort}
                currentSortKey={sortKey}
                sortDirection={sortDirection}
              />
              <SortableHeader
                columnKey="client"
                label="Client"
                onSort={handleSort}
                currentSortKey={sortKey}
                sortDirection={sortDirection}
              />
              <SortableHeader columnKey="status" label="Status" sortable={false} />
              <SortableHeader columnKey="lockedBy" label="Locked By" sortable={false} />
              <SortableHeader
                columnKey="lockedOn"
                label="Locked On"
                onSort={handleSort}
                currentSortKey={sortKey}
                sortDirection={sortDirection}
              />
              {showActionsColumn ? (
                <SortableHeader
                  columnKey="actions"
                  label="Actions"
                  sortable={false}
                  className="text-right"
                />
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary bg-surface-primary">
            {paginatedMonths.map((month) => (
              <tr key={month.id} className="hover:bg-surface-secondary/60">
                <td className="px-4 py-2 whitespace-nowrap text-text-primary">
                  <div className="font-medium">
                    {month.month} {month.year}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-text-secondary">{month.client}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {(() => {
                    const { container, icon } = getStatusStyles(month.status);
                    return (
                      <Typography className={container}>
                        <RiLock2Fill className={`w-3.5 h-3.5 ${icon}`} />
                        <Typography variant="span2">{month.status}</Typography>
                      </Typography>
                    );
                  })()}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-text-secondary">
                  {month.lockedBy || "—"}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-text-secondary">
                  {month.lockedOn ? formatDate(month.lockedOn) : "—"}
                </td>
                {showActionsColumn ? (
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    {String(month.status).toLowerCase() === "locked" ? (
                      canUnlockMonth ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => onOpenUnlockModal(month)}
                        >
                          Unlock
                        </Button>
                      ) : (
                        <Typography variant="span2" className="text-text-secondary">
                          —
                        </Typography>
                      )
                    ) : canLockMonth ? (
                      <Button size="sm" variant="primary" onClick={() => onOpenLockModal(month)}>
                        Lock
                      </Button>
                    ) : (
                      <Typography variant="span2" className="text-text-secondary">
                        —
                      </Typography>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
            {sortedData.length === 0 && !loading && (
              <tr>
                <td colSpan={tableColSpan} className="px-4 py-6 text-center text-text-secondary">
                  <p className="font-medium text-text-primary">
                    No month lock rows for this filter.
                  </p>
                  <p className="mt-1 text-sm">
                    Choose <strong>Month</strong> and <strong>Client</strong>, then click{" "}
                    <strong>Filter</strong> to load data from the server.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="p-4 border-t border-border-primary flex justify-end">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default MonthLockingSection;
